<template>
  <vue-markdown class="markdown" :html="false" :source="content" :breaks="true" :linkify="true" :anchor-attributes="{
    target: '_blank',
    rel: 'noopener noreferrer'
  }" />
</template>

<script>
import VueMarkdown from 'vue-markdown-v2';

export default {
  props: {
    content: String,
  },
  components: {
    VueMarkdown,
  },
  methods: {
    isInternalLink(href) {
      // Check if it's an internal link (starts with '/' and isn't an external link)
      return href && href.startsWith('/') && !href.match(/^(https?:|mailto:|tel:|ftp:)/);
    },
    setupInternalLinks() {
      // Get all links in the markdown content
      const links = this.$el.querySelectorAll('a');

      // Process each link
      links.forEach(link => {
        const href = link.getAttribute('href');

        if (this.isInternalLink(href)) {
          // For internal links, use Vue Router
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.$router.push(href);
          });
        }
      });
    }
  },
  mounted() {
    // Setup internal links after the component is rendered
    this.$nextTick(() => {
      this.setupInternalLinks();
    });
  }
};
</script>

<style lang="scss" scoped>
.markdown {
  --color-prettylights-syntax-comment: #57606a;
  --color-prettylights-syntax-constant: #0550ae;
  --color-prettylights-syntax-entity: #6639ba;
  --color-prettylights-syntax-storage-modifier-import: #24292f;
  --color-prettylights-syntax-entity-tag: #116329;
  --color-prettylights-syntax-keyword: #cf222e;
  --color-prettylights-syntax-string: #0a3069;
  --color-prettylights-syntax-variable: #953800;
  --color-prettylights-syntax-brackethighlighter-unmatched: #82071e;
  --color-prettylights-syntax-invalid-illegal-text: #f6f8fa;
  --color-prettylights-syntax-invalid-illegal-bg: #82071e;
  --color-prettylights-syntax-carriage-return-text: #f6f8fa;
  --color-prettylights-syntax-carriage-return-bg: #cf222e;
  --color-prettylights-syntax-string-regexp: #116329;
  --color-prettylights-syntax-markup-list: #3b2300;
  --color-prettylights-syntax-markup-heading: #0550ae;
  --color-prettylights-syntax-markup-italic: #24292f;
  --color-prettylights-syntax-markup-bold: #24292f;
  --color-prettylights-syntax-markup-deleted-text: #82071e;
  --color-prettylights-syntax-markup-deleted-bg: #ffebe9;
  --color-prettylights-syntax-markup-inserted-text: #116329;
  --color-prettylights-syntax-markup-inserted-bg: #dafbe1;
  --color-prettylights-syntax-markup-changed-text: #953800;
  --color-prettylights-syntax-markup-changed-bg: #ffd8b5;
  --color-prettylights-syntax-markup-ignored-text: #eaeef2;
  --color-prettylights-syntax-markup-ignored-bg: #0550ae;
  --color-prettylights-syntax-meta-diff-range: #8250df;
  --color-prettylights-syntax-brackethighlighter-angle: #57606a;
  --color-prettylights-syntax-sublimelinter-gutter-mark: #8c959f;
  --color-prettylights-syntax-constant-other-reference-link: #0a3069;
  --color-fg-default: var(--text-tertiary);
  --color-fg-muted: var(--text-tertiary);
  --color-fg-subtle: #6e7781;
  --color-canvas-default: var(--bg);
  // --color-canvas-subtle:#f6f8fa;
  --color-canvas-subtle: var(--secondary-bg);
  --color-border-default: #d0d7de;
  --color-border-muted: hsla(210, 18%, 87%, 1);
  --color-neutral-muted: rgba(175, 184, 193, 0.2);
  --color-accent-fg: #0969da;
  --color-accent-emphasis: #0969da;
  --color-success-fg: #1a7f37;
  --color-success-emphasis: #1f883d;
  --color-attention-fg: #9a6700;
  --color-attention-emphasis: #9a6700;
  --color-attention-subtle: var(--bg-warning);
  --color-danger-fg: #d1242f;
  --color-danger-emphasis: #cf222e;
  --color-done-fg: #8250df;
  --color-done-emphasis: #8250df;

  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  margin: 0;
  color: var(--color-fg-default);
  background-color: var(--color-canvas-default);
  font-size: 16px;
  line-height: 1.5;
  word-wrap: break-word;

  -webkit-overflow-scrolling: touch;
  /* Enable momentum scrolling on iOS */

  &::v-deep {
    .octicon {
      display: inline-block;
      fill: currentColor;
      vertical-align: text-bottom;
    }

    h1:hover .anchor .octicon-link:before,
    h2:hover .anchor .octicon-link:before,
    h3:hover .anchor .octicon-link:before,
    h4:hover .anchor .octicon-link:before,
    h5:hover .anchor .octicon-link:before,
    h6:hover .anchor .octicon-link:before {
      width: 16px;
      height: 16px;
      content: ' ';
      display: inline-block;
      background-color: currentColor;
      -webkit-mask-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' version='1.1' aria-hidden='true'><path fill-rule='evenodd' d='M7.775 3.275a.75.75 0 001.06 1.06l1.25-1.25a2 2 0 112.83 2.83l-2.5 2.5a2 2 0 01-2.83 0 .75.75 0 00-1.06 1.06 3.5 3.5 0 004.95 0l2.5-2.5a3.5 3.5 0 00-4.95-4.95l-1.25 1.25zm-4.69 9.64a2 2 0 010-2.83l2.5-2.5a2 2 0 012.83 0 .75.75 0 001.06-1.06 3.5 3.5 0 00-4.95 0l-2.5 2.5a3.5 3.5 0 004.95 4.95l1.25-1.25a.75.75 0 00-1.06-1.06l-1.25 1.25a2 2 0 01-2.83 0z'></path></svg>");
      mask-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' version='1.1' aria-hidden='true'><path fill-rule='evenodd' d='M7.775 3.275a.75.75 0 001.06 1.06l1.25-1.25a2 2 0 112.83 2.83l-2.5 2.5a2 2 0 01-2.83 0 .75.75 0 00-1.06 1.06 3.5 3.5 0 004.95 0l2.5-2.5a3.5 3.5 0 00-4.95-4.95l-1.25 1.25zm-4.69 9.64a2 2 0 010-2.83l2.5-2.5a2 2 0 012.83 0 .75.75 0 001.06-1.06 3.5 3.5 0 00-4.95 0l-2.5 2.5a3.5 3.5 0 004.95 4.95l1.25-1.25a.75.75 0 00-1.06-1.06l-1.25 1.25a2 2 0 01-2.83 0z'></path></svg>");
    }

    details,
    figcaption,
    figure {
      display: block;
    }

    summary {
      display: list-item;
    }

    [hidden] {
      display: none !important;
    }

    a {
      background-color: transparent;
      color: var(--color-accent-fg);
      text-decoration: none;
    }

    abbr[title] {
      border-bottom: none;
      -webkit-text-decoration: underline dotted;
      text-decoration: underline dotted;
    }

    b,
    strong {
      font-weight: var(--base-text-weight-semibold, 600);
    }

    dfn {
      font-style: italic;
    }

    h1 {
      margin: .67em 0;
      font-weight: var(--base-text-weight-semibold, 600);
      padding-bottom: .3em;
      font-size: 1.7em;
      border-bottom: 1px solid var(--color-border-muted);
    }

    mark {
      background-color: var(--color-attention-subtle);
      color: #181f25;
    }

    small {
      font-size: 90%;
    }

    sub,
    sup {
      font-size: 75%;
      line-height: 0;
      position: relative;
      vertical-align: baseline;
    }

    sub {
      bottom: -0.25em;
    }

    sup {
      top: -0.5em;
    }

    img {
      border-style: none;
      max-width: 100%;
      box-sizing: content-box;
      background-color: var(--color-canvas-default);
    }

    code,
    kbd,
    pre,
    samp {
      font-family: monospace;
      font-size: 1em;
    }

    figure {
      margin: 1em 40px;
    }

    hr {
      box-sizing: content-box;
      overflow: hidden;
      background: transparent;
      border-bottom: 1px solid var(--color-border-muted);
      height: .25em;
      padding: 0;
      margin: 24px 0;
      background-color: var(--color-border-default);
      border: 0;
    }

    input {
      font: inherit;
      margin: 0;
      overflow: visible;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
    }

    [type=button],
    [type=reset],
    [type=submit] {
      -webkit-appearance: button;
      appearance: button;
    }

    [type=checkbox],
    [type=radio] {
      box-sizing: border-box;
      padding: 0;
    }

    [type=number] {

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        height: auto;
      }
    }

    [type=search] {

      &::-webkit-search-cancel-button,
      &::-webkit-search-decoration {
        -webkit-appearance: none;
        appearance: none;
      }
    }

    ::-webkit-input-placeholder {
      color: inherit;
      opacity: .54;
    }

    ::-webkit-file-upload-button {
      -webkit-appearance: button;
      appearance: button;
      font: inherit;
    }

    a:hover {
      text-decoration: underline;
    }

    ::placeholder {
      color: var(--color-fg-subtle);
      opacity: 1;
    }

    hr {
      &::before {
        display: table;
        content: "";
      }

      &::after {
        display: table;
        clear: both;
        content: "";
      }
    }

    table {
      border-spacing: 0;
      border-collapse: collapse;
      display: block;
      width: max-content;
      max-width: 100%;
      overflow: auto;
    }

    td,
    th {
      padding: 0;
    }

    details {
      summary {
        cursor: pointer;
      }

      &:not([open])>*:not(summary) {
        display: none !important;
      }
    }

    a:focus,
    [role=button]:focus {
      outline: 2px solid var(--color-accent-fg);
      outline-offset: -2px;
      box-shadow: none;
    }

    input {

      &[type=radio]:focus,
      &[type=checkbox]:focus {
        outline: 2px solid var(--color-accent-fg);
        outline-offset: -2px;
        box-shadow: none;
      }
    }

    a:focus:not(:focus-visible),
    [role=button]:focus:not(:focus-visible) {
      outline: solid 1px transparent;
    }

    input {

      &[type=radio]:focus:not(:focus-visible),
      &[type=checkbox]:focus:not(:focus-visible) {
        outline: solid 1px transparent;
      }
    }

    a:focus-visible,
    [role=button]:focus-visible {
      outline: 2px solid var(--color-accent-fg);
      outline-offset: -2px;
      box-shadow: none;
    }

    input {

      &[type=radio]:focus-visible,
      &[type=checkbox]:focus-visible {
        outline: 2px solid var(--color-accent-fg);
        outline-offset: -2px;
        box-shadow: none;
      }
    }

    a:not([class]) {

      &:focus,
      &:focus-visible {
        outline-offset: 0;
      }
    }

    input {
      &[type=radio] {

        &:focus,
        &:focus-visible {
          outline-offset: 0;
        }
      }

      &[type=checkbox] {

        &:focus,
        &:focus-visible {
          outline-offset: 0;
        }
      }
    }

    kbd {
      display: inline-block;
      padding: 3px 5px;
      font: 11px ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
      line-height: 10px;
      color: var(--color-fg-default);
      vertical-align: middle;
      background-color: var(--color-canvas-subtle);
      border: solid 1px var(--color-neutral-muted);
      border-bottom-color: var(--color-neutral-muted);
      border-radius: 6px;
      box-shadow: inset 0 -1px 0 var(--color-neutral-muted);
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: 24px;
      margin-bottom: 16px;
      font-weight: var(--base-text-weight-semibold, 600);
      line-height: 1.25;
    }

    h2 {
      font-weight: var(--base-text-weight-semibold, 600);
      padding-bottom: .3em;
      font-size: 1.5em;
      border-bottom: 1px solid var(--color-border-muted);
    }

    h3 {
      font-weight: var(--base-text-weight-semibold, 600);
      font-size: 1.25em;
    }

    h4 {
      font-weight: var(--base-text-weight-semibold, 600);
      font-size: 1em;
    }

    h5 {
      font-weight: var(--base-text-weight-semibold, 600);
      font-size: .875em;
    }

    h6 {
      font-weight: var(--base-text-weight-semibold, 600);
      font-size: .85em;
      color: var(--color-fg-muted);
    }

    p {
      margin-top: 0;
      margin-bottom: 10px;
    }

    blockquote {
      margin: 0;
      padding: 0 1em;
      color: var(--color-fg-muted);
      border-left: 0.25em solid var(--color-border-default);
    }

    ul {
      margin-top: 0;
      margin-bottom: 0;
      padding-left: 2em;
      list-style: outside disc;
    }

    ol {
      margin-top: 0;
      margin-bottom: 0;
      padding-left: 2em;
      list-style: outside decimal;

      ol {
        list-style-type: lower-roman;
      }
    }

    ul {
      ol {
        list-style-type: lower-roman;
      }

      ul ol,
      ol ol {
        list-style-type: lower-alpha;
      }
    }

    ol {

      ul ol,
      ol ol {
        list-style-type: lower-alpha;
      }
    }

    dd {
      margin-left: 0;
    }

    tt,
    code,
    samp {
      font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
      font-size: 12px;
    }

    pre {
      margin-top: 0;
      margin-bottom: 0;
      font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
      font-size: 12px;
      word-wrap: normal;
    }

    .octicon {
      display: inline-block;
      overflow: visible !important;
      vertical-align: text-bottom;
      fill: currentColor;
    }

    input {

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        margin: 0;
        -webkit-appearance: none;
        appearance: none;
      }
    }

    .mr-2 {
      margin-right: var(--base-size-8, 8px) !important;
    }

    &::before {
      display: table;
      content: "";
    }

    &::after {
      display: table;
      clear: both;
      content: "";
    }

    >* {
      &:first-child {
        margin-top: 0 !important;
      }

      &:last-child {
        margin-bottom: 0 !important;
      }
    }

    a:not([href]) {
      color: inherit;
      text-decoration: none;
    }

    .absent {
      color: var(--color-danger-fg);
    }

    .anchor {
      float: left;
      padding-right: 4px;
      margin-left: -20px;
      line-height: 1;

      &:focus {
        outline: none;
      }
    }

    p,
    blockquote,
    ul,
    ol,
    dl,
    table,
    pre,
    details {
      margin-top: 0;
      margin-bottom: 16px;
    }

    blockquote> {
      :first-child {
        margin-top: 0;
      }

      :last-child {
        margin-bottom: 0;
      }
    }

    h1 .octicon-link,
    h2 .octicon-link,
    h3 .octicon-link,
    h4 .octicon-link,
    h5 .octicon-link,
    h6 .octicon-link {
      color: var(--color-fg-default);
      vertical-align: middle;
      visibility: hidden;
    }

    h1:hover .anchor,
    h2:hover .anchor,
    h3:hover .anchor,
    h4:hover .anchor,
    h5:hover .anchor,
    h6:hover .anchor {
      text-decoration: none;
    }

    h1:hover .anchor .octicon-link,
    h2:hover .anchor .octicon-link,
    h3:hover .anchor .octicon-link,
    h4:hover .anchor .octicon-link,
    h5:hover .anchor .octicon-link,
    h6:hover .anchor .octicon-link {
      visibility: visible;
    }

    h1 {

      tt,
      code {
        padding: 0 .2em;
        font-size: inherit;
      }
    }

    h2 {

      tt,
      code {
        padding: 0 .2em;
        font-size: inherit;
      }
    }

    h3 {

      tt,
      code {
        padding: 0 .2em;
        font-size: inherit;
      }
    }

    h4 {

      tt,
      code {
        padding: 0 .2em;
        font-size: inherit;
      }
    }

    h5 {

      tt,
      code {
        padding: 0 .2em;
        font-size: inherit;
      }
    }

    h6 {

      tt,
      code {
        padding: 0 .2em;
        font-size: inherit;
      }
    }

    summary {

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        display: inline-block;
      }

      h1 .anchor,
      h2 .anchor,
      h3 .anchor,
      h4 .anchor,
      h5 .anchor,
      h6 .anchor {
        margin-left: -40px;
      }

      h1,
      h2 {
        padding-bottom: 0;
        border-bottom: 0;
      }
    }

    ul.no-list {
      padding: 0;
      list-style-type: none;
    }

    ol {
      &.no-list {
        padding: 0;
        list-style-type: none;
      }

      &[type="a s"] {
        list-style-type: lower-alpha;
      }

      &[type="A s"] {
        list-style-type: upper-alpha;
      }

      &[type="i s"] {
        list-style-type: lower-roman;
      }

      &[type="I s"] {
        list-style-type: upper-roman;
      }

      &[type="1"] {
        list-style-type: decimal;
      }
    }

    div>ol:not([type]) {
      list-style-type: decimal;
    }

    ul {

      ul,
      ol {
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    ol {

      ol,
      ul {
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    li {
      >p {
        margin-top: 16px;
      }

      +li {
        margin-top: .25em;
      }
    }

    dl {
      padding: 0;

      dt {
        padding: 0;
        margin-top: 16px;
        margin-bottom: 8px;
        font-size: 1em;
        font-style: italic;
        font-weight: var(--base-text-weight-semibold, 600);
      }

      dd {
        padding: 0 16px;
        margin-bottom: 16px;
      }
    }

    table {
      th {
        font-weight: var(--base-text-weight-semibold, 600);
        padding: 6px 13px;
        border: 1px solid var(--color-border-default);
      }

      td {
        padding: 6px 13px;
        border: 1px solid var(--color-border-default);

        > :last-child {
          margin-bottom: 0;
        }
      }

      tr {
        background-color: var(--color-canvas-default);
        border-top: 1px solid var(--color-border-muted);

        &:nth-child(2n) {
          background-color: var(--color-canvas-subtle);
        }
      }

      img {
        background-color: transparent;
      }
    }

    img {
      &[align=right] {
        padding-left: 20px;
      }

      &[align=left] {
        padding-right: 20px;
      }
    }

    .emoji {
      max-width: none;
      vertical-align: text-top;
      background-color: transparent;
    }

    span {
      &.frame {
        display: block;
        overflow: hidden;

        >span {
          display: block;
          float: left;
          width: auto;
          padding: 7px;
          margin: 13px 0 0;
          overflow: hidden;
          border: 1px solid var(--color-border-default);
        }

        span {
          img {
            display: block;
            float: left;
          }

          span {
            display: block;
            padding: 5px 0 0;
            clear: both;
            color: var(--color-fg-default);
          }
        }
      }

      &.align-center {
        display: block;
        overflow: hidden;
        clear: both;

        >span {
          display: block;
          margin: 13px auto 0;
          overflow: hidden;
          text-align: center;
        }

        span img {
          margin: 0 auto;
          text-align: center;
        }
      }

      &.align-right {
        display: block;
        overflow: hidden;
        clear: both;

        >span {
          display: block;
          margin: 13px 0 0;
          overflow: hidden;
          text-align: right;
        }

        span img {
          margin: 0;
          text-align: right;
        }
      }

      &.float-left {
        display: block;
        float: left;
        margin-right: 13px;
        overflow: hidden;

        span {
          margin: 13px 0 0;
        }
      }

      &.float-right {
        display: block;
        float: right;
        margin-left: 13px;
        overflow: hidden;

        >span {
          display: block;
          margin: 13px auto 0;
          overflow: hidden;
          text-align: right;
        }
      }
    }

    code,
    tt {
      padding: .2em .4em;
      margin: 0;
      font-size: 85%;
      white-space: break-spaces;
      background-color: var(--color-neutral-muted);
      border-radius: 6px;
    }

    code br,
    tt br {
      display: none;
    }

    del code {
      text-decoration: inherit;
    }

    samp {
      font-size: 85%;
    }

    pre {
      code {
        font-size: 100%;
      }

      >code {
        padding: 0;
        margin: 0;
        word-break: normal;
        white-space: pre;
        background: transparent;
        border: 0;
      }
    }

    .highlight {
      margin-bottom: 16px;

      pre {
        margin-bottom: 0;
        word-break: normal;
        padding: 16px;
        overflow: auto;
        font-size: 85%;
        line-height: 1.45;
        color: var(--color-fg-default);
        background-color: var(--color-canvas-subtle);
        border-radius: 6px;
      }
    }

    pre {
      padding: 16px;
      overflow: auto;
      font-size: 85%;
      line-height: 1.45;
      color: var(--color-fg-default);
      background-color: var(--color-canvas-subtle);
      border-radius: 6px;

      code,
      tt {
        display: inline;
        max-width: auto;
        padding: 0;
        margin: 0;
        overflow: visible;
        line-height: inherit;
        word-wrap: normal;
        background-color: transparent;
        border: 0;
      }
    }

    .csv-data {

      td,
      th {
        padding: 5px;
        overflow: hidden;
        font-size: 12px;
        line-height: 1;
        text-align: left;
        white-space: nowrap;
      }

      .blob-num {
        padding: 10px 8px 9px;
        text-align: right;
        background: var(--color-canvas-default);
        border: 0;
      }

      tr {
        border-top: 0;
      }

      th {
        font-weight: var(--base-text-weight-semibold, 600);
        background: var(--color-canvas-subtle);
        border-top: 0;
      }
    }

    [data-footnote-ref] {
      &::before {
        content: "[";
      }

      &::after {
        content: "]";
      }
    }

    .footnotes-sep {
      display: none;
    }

    .footnotes {
      font-size: 12px;
      color: var(--color-fg-muted);
      border-top: 1px solid var(--color-border-default);

      ol {
        padding-left: 16px;

        ul {
          display: inline-block;
          padding-left: 16px;
          margin-top: 16px;
        }
      }

      li {
        position: relative;

        &:target {
          &::before {
            position: absolute;
            top: -8px;
            right: -8px;
            bottom: -8px;
            left: -24px;
            pointer-events: none;
            content: "";
            border: 2px solid var(--color-accent-emphasis);
            border-radius: 6px;
          }

          color: var(--color-fg-default);
        }
      }

      .data-footnote-backref g-emoji {
        font-family: monospace;
      }
    }

    .pl-c {
      color: var(--color-prettylights-syntax-comment);
    }

    .pl-c1,
    .pl-s .pl-v {
      color: var(--color-prettylights-syntax-constant);
    }

    .pl-e,
    .pl-en {
      color: var(--color-prettylights-syntax-entity);
    }

    .pl-smi,
    .pl-s .pl-s1 {
      color: var(--color-prettylights-syntax-storage-modifier-import);
    }

    .pl-ent {
      color: var(--color-prettylights-syntax-entity-tag);
    }

    .pl-k {
      color: var(--color-prettylights-syntax-keyword);
    }

    .pl-s,
    .pl-pds,
    .pl-s .pl-pse .pl-s1 {
      color: var(--color-prettylights-syntax-string);
    }

    .pl-sr {
      color: var(--color-prettylights-syntax-string);

      .pl-cce,
      .pl-sre,
      .pl-sra {
        color: var(--color-prettylights-syntax-string);
      }
    }

    .pl-v,
    .pl-smw {
      color: var(--color-prettylights-syntax-variable);
    }

    .pl-bu {
      color: var(--color-prettylights-syntax-brackethighlighter-unmatched);
    }

    .pl-ii {
      color: var(--color-prettylights-syntax-invalid-illegal-text);
      background-color: var(--color-prettylights-syntax-invalid-illegal-bg);
    }

    .pl-c2 {
      color: var(--color-prettylights-syntax-carriage-return-text);
      background-color: var(--color-prettylights-syntax-carriage-return-bg);
    }

    .pl-sr .pl-cce {
      font-weight: bold;
      color: var(--color-prettylights-syntax-string-regexp);
    }

    .pl-ml {
      color: var(--color-prettylights-syntax-markup-list);
    }

    .pl-mh {
      font-weight: bold;
      color: var(--color-prettylights-syntax-markup-heading);

      .pl-en {
        font-weight: bold;
        color: var(--color-prettylights-syntax-markup-heading);
      }
    }

    .pl-ms {
      font-weight: bold;
      color: var(--color-prettylights-syntax-markup-heading);
    }

    .pl-mi {
      font-style: italic;
      color: var(--color-prettylights-syntax-markup-italic);
    }

    .pl-mb {
      font-weight: bold;
      color: var(--color-prettylights-syntax-markup-bold);
    }

    .pl-md {
      color: var(--color-prettylights-syntax-markup-deleted-text);
      background-color: var(--color-prettylights-syntax-markup-deleted-bg);
    }

    .pl-mi1 {
      color: var(--color-prettylights-syntax-markup-inserted-text);
      background-color: var(--color-prettylights-syntax-markup-inserted-bg);
    }

    .pl-mc {
      color: var(--color-prettylights-syntax-markup-changed-text);
      background-color: var(--color-prettylights-syntax-markup-changed-bg);
    }

    .pl-mi2 {
      color: var(--color-prettylights-syntax-markup-ignored-text);
      background-color: var(--color-prettylights-syntax-markup-ignored-bg);
    }

    .pl-mdr {
      font-weight: bold;
      color: var(--color-prettylights-syntax-meta-diff-range);
    }

    .pl-ba {
      color: var(--color-prettylights-syntax-brackethighlighter-angle);
    }

    .pl-sg {
      color: var(--color-prettylights-syntax-sublimelinter-gutter-mark);
    }

    .pl-corl {
      text-decoration: underline;
      color: var(--color-prettylights-syntax-constant-other-reference-link);
    }

    g-emoji {
      display: inline-block;
      min-width: 1ch;
      font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
      font-size: 1em;
      font-style: normal !important;
      font-weight: var(--base-text-weight-normal, 400);
      line-height: 1;
      vertical-align: -0.075em;

      img {
        width: 1em;
        height: 1em;
      }
    }

    .task-list-item {
      list-style-type: none;

      label {
        font-weight: var(--base-text-weight-normal, 400);
      }

      &.enabled label {
        cursor: pointer;
      }

      +.task-list-item {
        margin-top: 4px;
      }

      .handle {
        display: none;
      }
    }

    .task-list-item-checkbox {
      margin: 0 .2em .25em -1.4em;
      vertical-align: middle;
    }

    .contains-task-list {
      &:dir(rtl) .task-list-item-checkbox {
        margin: 0 -1.6em .25em .2em;
      }

      position: relative;

      &:hover .task-list-item-convert-container,
      &:focus-within .task-list-item-convert-container {
        display: block;
        width: auto;
        height: 24px;
        overflow: visible;
        clip: auto;
      }
    }

    ::-webkit-calendar-picker-indicator {
      filter: invert(50%);
    }

    .markdown-alert {
      padding: var(--base-size-8) var(--base-size-16);
      margin-bottom: 16px;
      color: inherit;
      border-left: 0.25em solid var(--color-border-default);

      > {
        :first-child {
          margin-top: 0;
        }

        :last-child {
          margin-bottom: 0;
        }
      }

      .markdown-alert-title {
        display: flex;
        font-weight: var(--base-text-weight-medium, 500);
        align-items: center;
        line-height: 1;
      }

      &.markdown-alert-note {
        border-left-color: var(--color-accent-emphasis);

        .markdown-alert-title {
          color: var(--color-accent-fg);
        }
      }

      &.markdown-alert-important {
        border-left-color: var(--color-done-emphasis);

        .markdown-alert-title {
          color: var(--color-done-fg);
        }
      }

      &.markdown-alert-warning {
        border-left-color: var(--color-attention-emphasis);

        .markdown-alert-title {
          color: var(--color-attention-fg);
        }
      }

      &.markdown-alert-tip {
        border-left-color: var(--color-success-emphasis);

        .markdown-alert-title {
          color: var(--color-success-fg);
        }
      }

      &.markdown-alert-caution {
        border-left-color: var(--color-danger-emphasis);

        .markdown-alert-title {
          color: var(--color-danger-fg);
        }
      }
    }

    /* Ensure links are easily tappable on mobile */
    a {
      padding: 2px 0;
      display: inline-block;
    }
  }
}
</style>
